import React, { useState, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { MoreVertical } from "lucide-react";
import Icon from "shared/components/Icon";

/**
 * Unified card shell for all board card types.
 * All colors via CSS design tokens — works in both light and dark mode.
 */
export default function BoardCardShell({
  title,
  subtitle,
  badge,
  actions = [],
  footer,
  readOnly = false,
  children,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState(null);
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  const openMenu = useCallback(() => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    setMenuPos({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
    setMenuOpen(true);
  }, []);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (
        menuRef.current && !menuRef.current.contains(e.target) &&
        btnRef.current && !btnRef.current.contains(e.target)
      ) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  return (
    <div className="flex flex-col w-full h-full min-h-0 group/shell">
      {/* ── Header ──────────────────────────────────────────── */}
      <div
        className="flex-shrink-0 flex items-center gap-0 relative"
        style={{ minHeight: 40, paddingRight: readOnly ? 12 : 36 }}
      >
        {/* Drag handle: hover-reveal, 28px wide */}
        {!readOnly && (
          <div
            className="board-drag-handle flex-shrink-0 flex items-center justify-center self-stretch opacity-0 group-hover/shell:opacity-100 transition-opacity duration-150 cursor-grab active:cursor-grabbing select-none text-[color:var(--text-muted)]"
            style={{ width: 28 }}
            title="Drag to move"
          >
            <SixDotGrip />
          </div>
        )}

        {/* Title + subtitle */}
        <div
          className="flex-1 min-w-0 flex flex-col justify-center"
          style={{ padding: readOnly ? "8px 0 8px 12px" : "8px 0 8px 4px" }}
        >
          <h3
            className="font-semibold truncate leading-tight text-[color:var(--text-primary)]"
            style={{ fontSize: 13, fontFamily: "Inter, sans-serif" }}
            title={title}
          >
            {title || "Untitled"}
          </h3>
          {subtitle ? (
            <p
              className="truncate mt-0.5 leading-tight text-[color:var(--text-muted)]"
              style={{ fontSize: 11, fontFamily: "Inter, sans-serif" }}
            >
              {subtitle}
            </p>
          ) : null}
        </div>

        {/* Type badge: hover-only */}
        {badge ? (
          <span
            className="flex-shrink-0 mr-1 opacity-0 group-hover/shell:opacity-100 transition-opacity duration-150 select-none pointer-events-none"
            style={{
              fontSize: 10,
              fontWeight: 500,
              borderRadius: 9999,
              padding: "1px 6px",
              background: badge.bg,
              color: badge.color,
              letterSpacing: "0.02em",
              fontFamily: "Inter, sans-serif",
            }}
          >
            {badge.label}
          </span>
        ) : null}

        {/* ⋮ menu button */}
        {!readOnly && actions.length > 0 ? (
          <div className="absolute right-2 top-1/2 -translate-y-1/2">
            <button
              ref={btnRef}
              type="button"
              onClick={openMenu}
              className="p-1 rounded-md text-[color:var(--text-muted)] opacity-0 group-hover/shell:opacity-70 hover:!opacity-100 hover:bg-surface-hover transition-all"
              title="More options"
            >
              <MoreVertical className="w-4 h-4" />
            </button>
          </div>
        ) : null}
      </div>

      {/* Divider */}
      <div className="flex-shrink-0 w-full" style={{ height: 1, backgroundColor: "var(--border-default)" }} />

      {/* ── Chart body ──────────────────────────────────────── */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {children}
      </div>

      {/* ── Footer (optional) ───────────────────────────────── */}
      {footer ? (
        <>
          <div className="flex-shrink-0 w-full" style={{ height: 1, backgroundColor: "var(--border-default)" }} />
          <div className="flex-shrink-0" style={{ maxHeight: "45%", overflowY: "auto" }}>
            {footer}
          </div>
        </>
      ) : null}

      {/* ── Portal-rendered dropdown menu ── */}
      {menuOpen && menuPos && typeof document !== "undefined" && createPortal(
        <div
          ref={menuRef}
          className="fixed overflow-hidden rounded-md border border-[color:var(--border-default)] bg-surface py-1 shadow-token-lg"
          style={{
            top: menuPos.top,
            right: menuPos.right,
            minWidth: 160,
            zIndex: 99999,
            fontFamily: "Inter, sans-serif",
          }}
        >
          {actions.map((action, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { setMenuOpen(false); action.onClick(); }}
              className={`w-full px-3 py-2 text-left text-[12px] flex items-center gap-2 transition-colors ${action.danger ? "text-[color:var(--feedback-error)] hover:bg-[color:color-mix(in_srgb,var(--feedback-error)_6%,transparent)]" : "text-[color:var(--text-primary)] hover:bg-surface-hover"}`}
              style={{ fontFamily: "Inter, sans-serif" }}
            >
              {action.icon ? (
                <Icon name={action.icon} size={14} color={action.danger ? "var(--feedback-error)" : "var(--text-muted)"} />
              ) : null}
              {action.label}
            </button>
          ))}
        </div>,
        document.body
      )}
    </div>
  );
}

function SixDotGrip() {
  return (
    <svg width="12" height="16" viewBox="0 0 12 16" fill="currentColor">
      <circle cx="3.5" cy="3.5" r="1.5" />
      <circle cx="8.5" cy="3.5" r="1.5" />
      <circle cx="3.5" cy="8"   r="1.5" />
      <circle cx="8.5" cy="8"   r="1.5" />
      <circle cx="3.5" cy="12.5" r="1.5" />
      <circle cx="8.5" cy="12.5" r="1.5" />
    </svg>
  );
}
